//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  FacebookPageEngagementSortDir,
  FacebookPageEngagementSortKey,
} from '@facebook/constants/facebookEngagementConstants';
import { IFacebookPageMetadata } from '@facebook/models/facebookPostDetailsModel';
import {
  IPageAudienceMetricsResponse,
  IPageMetricsResponse,
  IRecentPost,
} from '@facebook/models/facebookStateModel';
import FacebookPageId from '@models/FacebookPageId';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';

const API_BASE_POST_CSV = '/engagement/posts.csv';
const API_BASE_AUDIENCE_CSV = '/audience.csv';
export const API_DATE_FORMAT = 'YYYY-MM-DD';

function fmtDate(date: Date): string {
  return moment(date).format(API_DATE_FORMAT);
}

export interface IRecentPostsResponse {
  posts: IRecentPost[];
  totalCount: number;
}

export interface IPostsInRangeParams {
  startDate: Date;
  endDate: Date;
  limit: number;
  offset: number;
  sortKey: FacebookPageEngagementSortKey;
  sortDirection: FacebookPageEngagementSortDir;
}

export interface IFacebookScheduleLinkMetadata {
  description?: string;
  imageUrl?: string;
  title?: string;
  url?: string;
}

function gatewayFacebookFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<Response> {
  return serializedGatewayFetch(
    'facebook_gateway',
    `/api/v1/facebook/${endpoint}`,
    init,
  );
}

function gatewayFacebookPageFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<Response> {
  return serializedGatewayFetch(
    'facebook_gateway',
    `/api/v1/facebook/page/${endpoint}`,
    init,
  );
}

/**
 * Gets metadata for a brand's Facebook page (bio, username, profile pic, etc.)
 *
 * GET /api/v1/facebook/page/:pageId/details
 */
export async function getPageMetadata(
  pageId: FacebookPageId,
): Promise<IFacebookPageMetadata> {
  const res = await gatewayFacebookPageFetch(`${pageId}/details`);
  return await res.json();
}

/**
 * Gets Facebook onboarded state for client
 *
 * GET /api/v1/facebook/is-onboarded
 */
export async function getClientIsOnboarded(): Promise<boolean> {
  const res = await gatewayFacebookFetch('is-onboarded');
  return await res.json();
}

/**
 * Sets Facebook onboarded state for client
 *
 * POST /api/v1/facebook/set-onboarded
 */
export async function setOnboarded(): Promise<boolean> {
  const res = await gatewayFacebookFetch('set-onboarded', {
    method: 'POST',
  });
  return await res.json();
}

/**
 * Gets recent posts for a brand's Facebook page
 * to display on the Summary page.
 *
 * GET /api/v1/facebook/page/:pageId/summary/posts
 */
export async function getRecentPosts(
  pageId: FacebookPageId,
  limit: number,
): Promise<IRecentPostsResponse> {
  const queryParams = makeParams({
    limit,
    offset: 0,
    sortKey: FacebookPageEngagementSortKey.PUBLISH_DATE,
    sortDirection: FacebookPageEngagementSortDir.DESC,
  });
  const res = await gatewayFacebookPageFetch(
    `${pageId}/summary/posts?${queryParams}`,
  );
  return await res.json();
}

/**
 * Gets recent top-level stats for a brand's Facebook page
 * to display on the Summary page.
 *
 * GET /api/v1/facebook/page/:pageId/summary/stats
 */
export async function getSummaryStats(
  pageId: FacebookPageId,
): Promise<IPageMetricsResponse> {
  // Summary stats are always calculated for the last 7 days
  const startDate = moment().subtract(6, 'day').format(API_DATE_FORMAT);
  const endDate = moment().format(API_DATE_FORMAT);
  const queryParams = makeParams({
    startDate,
    endDate,
  });
  const res = await gatewayFacebookPageFetch(
    `${pageId}/summary/stats?${queryParams}`,
  );
  return await res.json();
}

/**
 * Gets posts for a brand's Facebook page from within a given date range.
 * Supports sorting and pagination.
 *
 * GET /api/v1/facebook/page/:pageId/engagement/posts
 */
export async function getPostsInRange(
  pageId: FacebookPageId,
  params: IPostsInRangeParams,
): Promise<IRecentPostsResponse> {
  const queryParams = makeParams({
    ...params,
    startDate: fmtDate(params.startDate),
    endDate: fmtDate(params.endDate),
  });

  const res = await gatewayFacebookPageFetch(
    `${pageId}/engagement/posts?${queryParams}`,
  );
  return await res.json();
}

/**
 * Gets top-level stats for a brand's Facebook page over a given date range.
 *
 * GET /api/v1/facebook/page/:pageId/engagement/stats
 */
export async function getEngagementStats(
  pageId: FacebookPageId,
  startDate: Date,
  endDate: Date,
  init?: RequestInit,
): Promise<IPageMetricsResponse> {
  const queryParams = makeParams({
    startDate: fmtDate(startDate),
    endDate: fmtDate(endDate),
  });
  const res = await gatewayFacebookPageFetch(
    `${pageId}/engagement/stats?${queryParams}`,
    init,
  );
  return await res.json();
}

/**
 * Gets audience demographic stats for a brand's Facebook page
 * to display on the Audience page.
 *
 * GET /api/v1/facebook/page/:pageId/audience
 */
export async function getAudienceStats(
  pageId: FacebookPageId,
  startDate: Date,
  endDate: Date,
  init?: RequestInit,
): Promise<IPageAudienceMetricsResponse> {
  const params = {
    pageId,
    startDate: fmtDate(startDate),
    endDate: fmtDate(endDate),
  };
  const queryParams = makeParams(params);
  const res = await gatewayFacebookPageFetch(
    `${pageId}/audience?${queryParams}`,
    init,
  );
  return await res.json();
}

/**
 * Get from Facebook the metadata for the link preview
 *
 * POST /api/v1/facebook/link-metadata
 */
export async function getLinkMetadata(
  url: string,
): Promise<IFacebookScheduleLinkMetadata> {
  const res = await gatewayFacebookFetch(`link-metadata`, {
    method: 'POST',
    body: JSON.stringify({
      url,
    }),
  });
  return await res.json();
}

/**
 * Download post csv export
 *
 * GET /api/v1/facebook/page/{pageId}/engagement/posts.csv
 */
export async function downloadPostCSV(
  pageId: FacebookPageId,
  dateRange: IUnisonDateRange,
): Promise<string> {
  const params = {
    startDate: moment(dateRange.startDate).format(API_DATE_FORMAT),
    endDate: moment(dateRange.endDate).format(API_DATE_FORMAT),
  };
  const queryParams = makeParams(params);
  const res = gatewayFacebookPageFetch(
    `${pageId}${API_BASE_POST_CSV}?${queryParams}`,
  );

  const response = await res;
  return await response.text();
}

/**
 * Download audience csv export
 *
 * GET /api/v1/facebook/page/{pageId}/audience.csv
 */
export async function downloadAudienceCSV(
  pageId: FacebookPageId,
  dateRange: IUnisonDateRange,
): Promise<string> {
  const params = {
    startDate: moment(dateRange.startDate).format(API_DATE_FORMAT),
    endDate: moment(dateRange.endDate).format(API_DATE_FORMAT),
  };
  const queryParams = makeParams(params);
  const res = gatewayFacebookPageFetch(
    `${pageId}${API_BASE_AUDIENCE_CSV}?${queryParams}`,
  );

  const response = await res;
  return await response.text();
}
